import React, {useEffect, useState} from "react";
import {useForm} from 'react-hook-form';
import {MenuSetMenu} from "../../../../types/admin/menuSet";
import {MenuListInput} from "./MenuListInput";
import {SelectLabelSetModal} from "../LabelSet/SelectLabelSetModal";
import {LabelSet} from "../../../../types/admin/labelSet";
import useAppRouter from "../../../../hooks/useAppRouter";
import {ApiClientError} from "../../../../apis/errors";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import useLoading from "../../../../hooks/useLoading";

export interface MenuSetFormValues {
  name: string;
  content: string;
  labelSetId: number | null;
  labelSetName: string | null;
  menuList: MenuSetMenu[];
}

interface MenuSetFormProps {
  defaultValues: MenuSetFormValues;
  isEdit: boolean;
  apiClientError: ApiClientError | null,
  clearApiClientError: () => Promise<void>;
  onSave: (data: MenuSetFormValues) => Promise<void>;
}

export const MenuSetForm = (props: MenuSetFormProps) => {
  const { defaultValues, isEdit, apiClientError, clearApiClientError, onSave } = props;
  // React Hook Formでは循環参照をもつオブジェクトを扱えない為、メニューリストはsetStateで管理する
  const [menuList, setMenuList] = useState<MenuSetMenu[]>(defaultValues.menuList);
  const [showSelectLabelSetModal, setSowSelectLabelSetModal] = useState<boolean>(false);
  const [existsSelectedLabel, setExistsSelectedLabel] = useState<boolean>(false);
  const { goto } = useAppRouter();
  const withLoading = useLoading();

  const {
    register,
    setValue,
    clearErrors,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: defaultValues.name,
      content: defaultValues.content,
      labelSetId: defaultValues.labelSetId,
      labelSetName: defaultValues.labelSetName,
      // React Hook Formでは循環参照をもつオブジェクトを扱えない為、メニューリストはsetStateで管理する
      // ただし、バリデーション登録の為に項目を用意しておく。
      menuList: null,
    },
    reValidateMode: 'onSubmit',
  });
  const labelSetId = watch('labelSetId');
  const labelSetName = watch('labelSetName');

  useEffect(() => {
    clearErrors('menuList');
  }, [clearErrors, menuList]);

  const handleShowSelectLabelSetModalClick = async (): Promise<void> => {
    setSowSelectLabelSetModal(true);
  };

  const handleSelectLabelSet = async (labelSet: LabelSet) => {
    await clearErrors('labelSetId');
    setValue('labelSetId', labelSet.id);
    setValue('labelSetName', labelSet.name);
  };

  const handleSave = async (): Promise<void> => {
    await withLoading(async () => {
      await clearApiClientError();
      await handleSubmit(async (data: MenuSetFormValues) => {
        await onSave({
          name: data.name,
          content: data.content,
          labelSetId: data.labelSetId,
          labelSetName: data.labelSetName,
          menuList,
        });
      })();
    });
  };

  return (
    <>
      {showSelectLabelSetModal && (
        <SelectLabelSetModal
          {...{
            setShowModal: setSowSelectLabelSetModal,
            onSelect: handleSelectLabelSet,
            onSelected: async () => { setSowSelectLabelSetModal(false); }
          }}
        />
      )}
      <form className="flex flex-col bg-sub min-h-full px-8">
        <div className="text-xl text-primary font-semibold py-6">メニューセット{ isEdit ? '編集' : '作成' } </div>
        <div className="mb-4 text-danger text-xs">
          { apiClientError?.message }
          { (Object.keys(errors).length > 0) && '入力エラーがあります' }
        </div>

        { /* 名前 */ }
        <div className="mb-4">
          <label htmlFor="name">
            <div className="text-sm font-semibold text-primary mb-1">
              <span>名前</span>
              <span className="ml-2 required">必須</span>
            </div>
            <input
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register("name",
                makeRegisterOptions('名前', [
                  { type: FormValidationType.REQUIRED },
                  { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                ])
              )}
              id="name"
              className="text-field"
              type="text"
              maxLength={100}
            />
            <div className="mt-1 text-danger text-xs">
              {errors.name?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'name',
              }}
            />
          </label>
        </div>

        { /* 説明 */ }
        <div className="mb-4">
          <label htmlFor="content">
            <div className="text-sm font-semibold text-primary mb-1">
              説明
            </div>
            <textarea
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register('content',
                makeRegisterOptions('説明', [
                  { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                ])
              )}
              className="text-field"
              id="content"
              maxLength={255}
            />
            <div className="mt-1 text-danger text-xs">
              {errors.content?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'content',
              }}
            />
          </label>
        </div>

        <div className="mb-4">
          <label htmlFor="labelSetId">
            <div className="text-sm font-semibold text-primary">
              <span>ラベルセット</span>
              {!isEdit && (
                <span className="ml-2 required">必須</span>
              )}
            </div>
            <div className="flex items-center pt-2 pl-2">
              <div className="text-sm">{labelSetName}</div>
              {!isEdit && (
                <div className={labelSetName === null ? '' : 'ml-4'}>
                  <button
                    className={`btn-primary ${existsSelectedLabel ? 'btn-primary-disabled' : ''} `}
                    type="button"
                    onClick={async () => {
                      await handleShowSelectLabelSetModalClick();
                    }}
                    disabled={existsSelectedLabel}
                  >
                    選 択
                  </button>
                </div>
              )}
            </div>
            <input
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register('labelSetName')}
              id="labelSetName"
              type="hidden"
            />
            <input
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...register('labelSetId',
                makeRegisterOptions('ラベルセット', [
                  { type: FormValidationType.REQUIRED },
                ])
              )}
              id="labelSetId"
              type="hidden"
            />
            <div className="mt-1 text-danger text-xs">
              {errors.labelSetId?.message}
            </div>
            <ValidationErrorMessages
              {...{
                fieldErrors: apiClientError?.errors,
                field: 'labelSetId',
              }}
            />
          </label>
        </div>

        { /* メニュー */ }
        <div className='py-2'>
          <MenuListInput
            {...{
              value: menuList,
              labelSetId: labelSetId === null ? undefined : labelSetId,
              onChange: async (newMenuList: MenuSetMenu[]) => {
                setMenuList(newMenuList);
              },
              setExistsSelectedLabel,
            }}
          />
          <input
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...register('menuList', {
              validate: {
                requiredMenu: value => (menuList.length > 0) && !value
              }
            })}
            type="hidden"
          />
          {errors.menuList && errors.menuList.type === 'requiredMenu' && (
            <span className="my-2 text-red-500 text-xs">
              メニューは必須です
            </span>
          )}
          <ValidationErrorMessages
            {...{
              fieldErrors: apiClientError?.errors,
              field: 'menuList',
            }}
          />
        </div>

        <div className="sticky flex bottom-0 bg-sub mt-auto py-4">
          <button
            className="btn-sub"
            type='button'
            onClick={() => goto('menuSetList')}
          >
            キャンセル
          </button>
          <button
            className="btn-primary ml-auto"
            type='button'
            onClick={handleSave}
          >
            保 存
          </button>
        </div>
      </form>
    </>
  );
};
